import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  clamp,
  countBy,
  flatten,
  groupBy,
  head,
  keys,
  last,
  map,
  orderBy,
  padStart,
  reduce,
  replace,
  reverse,
  round,
  size,
  uniq,
  values,
} from 'lodash';
import SvgIcon from '@mui/material/SvgIcon';

import { Dictionary } from 'highcharts';

import { Timescale } from 'src/types/models';

import ChartRangePicker from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/RangePickers/ChartRangePicker';
import { getColors } from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/Charts/colors';

import ColumnChart from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/Charts/ColumnChart';

import StackedColumnChart from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/Charts/StackedColumnChart';

import StackedPercentColumnChart from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/Charts/StackedPercentColumnChart';

import {
  IChartData,
  IChartDataDate,
  IChartDataSeries,
} from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel';

import LineChart, { colors } from './LineChart';
import { CHART_FRAME_SX } from './chartFrame';
import OutputsTable from './OutputsTable';

const filteredItems = <T extends { id: number }>(items: T[]): T[] =>
  items.filter(
    (item, index, self) => index === self.findIndex((t) => t.id === item.id)
  );

interface IGenerateChartPanelProps {
  chartData: IChartData;
  refreshedChartData: IChartData;
  timescale: Timescale;
  formatKey?: number;
  yAxisTitle: string;
  chartTitle: string;
  prefix: string;
  suffix: string;
  magnitudeTruncation: number;
  outputId?: number;
  modelInstanceId?: number;
  decimalPlaces: number;
  chartDateType: string;
  selectedChartRange: [string, string] | null;
  selectedChartType: string;
  selectedChartDataType: string;
  handleChangeChartRange: (startRange: string, endRange: string) => void;
  handleChangeChartType: (chartType: string) => void;
  handleChangeChartDataType: (chartDataType: string) => void;
  handleLegendChange: (id: number, visible: boolean) => void;
  isPanelCollapsed: boolean;
  showScenarios: boolean;
  // Optional external handle onto the Original/Recalculated table's
  // export API (OutputsTableRef) — the exec What-If RESULT screen wires
  // its "Download (Excel)" to exportToExcel through this. When absent,
  // the internal (currently unused, fork-inherited) ref is kept.
  tableExportRef?: React.Ref<OutputsTableRef>;
  // Drop the outer Container's 24px gutters so chart + table sit flush
  // with the host surface's own left edge — the exec What-If RESULT
  // screen needs its content aligned with its flush headline (2026-08-22
  // alignment ruling). Default false: analyst cards keep their gutters.
  disableGutters?: boolean;
  // Exec chart frame (2026-09-07): draw the Tornado-style outlined frame around the chart block (chart + legend +
  // range picker). The data table stays outside, so the frame's edges line up with the table's. Analyst cards omit it.
  frameChart?: boolean;
}

export const chartTypes = [
  {
    id: 'line',
    label: 'Line Chart',
  },
  {
    id: 'column',
    label: 'Column Chart',
  },
  {
    id: 'stackedColumn',
    label: 'Stacked Column Chart',
  },
  {
    id: 'stackedPercentColumn',
    label: 'Stacked Percent Column Chart',
  },
];

interface IChartDataTypes {
  id: 'all' | 'preview' | 'published';
  label: string;
  icon: JSX.Element | null;
}

export const chartDataTypes: IChartDataTypes[] = [
  {
    id: 'all',
    label: 'All Data',
    icon: null,
  },
  {
    id: 'preview',
    label: 'Recalculated Data',
    icon: (
      <SvgIcon fontSize="small" viewBox="0 0 30 12">
        <line x1="0" y1="3" x2="30" y2="3" stroke="black" strokeDasharray="4" />
      </SvgIcon>
    ),
  },
  {
    id: 'published',
    label: 'Original Data',
    icon: (
      <SvgIcon fontSize="small" viewBox="0 0 30 12">
        <line x1="0" y1="1" x2="30" y2="1" stroke="black" />
      </SvgIcon>
    ),
  },
];

export interface OutputsTableRef {
  exportToExcel: () => void;
  exportToCSV: () => void;
}

const GenerateChartPanel: FunctionComponent<IGenerateChartPanelProps> = ({
  timescale,
  chartData,
  formatKey,
  yAxisTitle,
  prefix,
  suffix,
  magnitudeTruncation,
  outputId,
  modelInstanceId,
  decimalPlaces,
  chartDateType,
  chartTitle,
  selectedChartRange,
  handleChangeChartRange,
  refreshedChartData,
  selectedChartType,
  handleChangeChartType,
  handleLegendChange,
  isPanelCollapsed,
  showScenarios,
  selectedChartDataType,
  handleChangeChartDataType,
  tableExportRef,
  disableGutters = false,
  frameChart = false,
}) => {
  const printChartRef = useRef<HTMLDivElement>();

  const outputsTableRef = useRef<OutputsTableRef>(null);

  const scenariosCount = size(
    countBy(refreshedChartData.PublishedData, 'scenario')
  );

  const [groupedSeries, setGroupedSeries] = useState<
    Dictionary<
      {
        dataType: string;
        scenario: string;
        segment: string;
        name: string;
        values: number[];
        id: number;
        output?: string | undefined;
        visible: boolean;
        dashStyle?: string | undefined;
      }[]
    >
  >({});

  useEffect(() => {
    if (selectedChartDataType === 'all') {
      setGroupedSeries(
        scenariosCount <= 1
          ? groupBy(
              [
                ...map(chartData.PreviewData, (i) => ({
                  ...i,
                  dataType: 'preview',
                })),
                ...map(chartData.PublishedData, (i) => ({
                  ...i,
                  dataType: 'published',
                })),
              ],
              (i) => i.segment.split(',')[0]
            )
          : groupBy(
              [
                ...map(chartData.PreviewData, (i) => ({
                  ...i,
                  dataType: 'preview',
                })),
                ...map(chartData.PublishedData, (i) => ({
                  ...i,
                  dataType: 'published',
                })),
              ],
              'scenario'
            )
      );
    } else if (selectedChartDataType === 'published') {
      setGroupedSeries(
        scenariosCount <= 1
          ? groupBy(
              [
                ...map(chartData.PublishedData, (i) => ({
                  ...i,
                  dataType: 'published',
                })),
              ],
              (i) => i.segment.split(',')[0]
            )
          : groupBy(
              [
                ...map(chartData.PublishedData, (i) => ({
                  ...i,
                  dataType: 'published',
                })),
              ],
              'scenario'
            )
      );
    } else if (selectedChartDataType === 'preview') {
      setGroupedSeries(
        scenariosCount <= 1
          ? groupBy(
              [
                ...map(chartData.PreviewData, (i) => ({
                  ...i,
                  dataType: 'preview',
                })),
              ],
              (i) => i.segment.split(',')[0]
            )
          : groupBy(
              [
                ...map(chartData.PreviewData, (i) => ({
                  ...i,
                  dataType: 'preview',
                })),
              ],
              'scenario'
            )
      );
    }
  }, [selectedChartDataType, chartData]);

  // Memoized: this chain is the input to every chart variant + the
  // OutputsTable. Without memo, every parent re-render (including
  // every keystroke in the chat input above) rebuilt these arrays,
  // generating a new reference and triggering the chart's options
  // effect — which re-initialized Highcharts and dismissed any open
  // tooltip / hover state. groupedSeries + scenariosCount + chartData
  // are themselves stable across keystrokes (useState + derived from
  // state), so the memo is sound.
  const seriesWithColors = useMemo(
    () =>
      reduce(
        keys(groupedSeries),
        (a, c, idx) => {
          a[c] = map(groupedSeries[c], (j, jidx, arr) => {
            const color = getColors(arr.length);
            return {
              ...j,
              color:
                scenariosCount <= 1
                  ? colors[j.id - 1]
                  : (color && (color.get(idx) as string[]))[jidx % arr.length],
            };
          });
          return a;
        },
        {} as Record<
          string,
          (IChartDataSeries & {
            color: string;
            dataType: string;
          })[]
        >
      ),
    [groupedSeries, scenariosCount]
  );

  const segmentsWithColors = useMemo(() => {
    const uniqueSegments = uniq(map(chartData.PublishedData, 'segment'));
    return reduce(
      uniqueSegments,
      (a, c, idx) => {
        a[c] = colors[idx];
        return a;
      },
      {} as Record<string, string>
    );
  }, [chartData.PublishedData]);

  const adjustColor = (color: string, amount: number) => {
    // Validate the amount range
    amount = clamp(amount, -1, 1);

    if (amount === 0) {
      return color;
    }

    // Remove the "#" prefix from the hex color code
    color = replace(color, '#', '');

    // Convert the hex color code to RGB values
    let r = parseInt(color.substring(0, 2), 16);
    let g = parseInt(color.substring(2, 4), 16);
    let b = parseInt(color.substring(4, 6), 16);

    // Calculate the adjusted values
    r = round(r + amount * (amount < 0 ? r : 255 - r));
    g = round(g + amount * (amount < 0 ? g : 255 - g));
    b = round(b + amount * (amount < 0 ? b : 255 - b));

    // Convert the adjusted RGB values back to hex format
    // Return the adjusted color
    return (
      '#' +
      padStart(r.toString(16), 2, '0') +
      padStart(g.toString(16), 2, '0') +
      padStart(b.toString(16), 2, '0')
    );
  };

  // Memoized: drives every chart variant's series prop. Outer map
  // mutates a per-build segmentCounter (declared inside the memo so
  // it doesn't leak across recomputes). Re-runs only when the
  // underlying series, color map, or scenario shape change.
  const groupedSeriesListWithColors = useMemo(() => {
    const flat = orderBy(
      flatten(values(seriesWithColors)),
      ['segment'],
      ['asc']
    );
    // eslint-disable-next-line no-console
    console.log(
      '[ESChart] series entering groupedSeriesListWithColors:',
      flat.length,
      flat.map((s) => `${s.segment}:${s.dataType}`)
    );
    const segmentCounter: Record<string, number> = {};
    return map(flat, (obj, idx) => {
      const alphaValues = [
        ...(scenariosCount === 1 ? [0] : []),
        ...(scenariosCount === 2 ? reverse([0, 0.2]) : []),
        ...(scenariosCount === 3 ? reverse([-0.2, 0, 0.2]) : []),
      ];
      // s values for France segments
      const segment = obj.segment;
      const color = segmentsWithColors[segment];

      if (segmentCounter[segment] === undefined) {
        segmentCounter[segment] = 0;
      }

      const segmentCount = segmentCounter[segment];

      const alphaIndex = segmentCount % alphaValues.length; // Adjust alpha index calculation
      const alphaValue = alphaValues[alphaIndex];
      segmentCounter[segment]++;

      // Determine line style based on data type and selection
      let dashStyle: string;
      if (!showScenarios) {
        if (selectedChartDataType === 'published') {
          // Original data only - always solid
          dashStyle = 'Solid';
        } else if (selectedChartDataType === 'preview') {
          // Recalculated data only - always dotted
          dashStyle = 'ShortDot';
        } else {
          // All data - solid for published, dotted for preview
          dashStyle = obj.dataType === 'published' ? 'Solid' : 'ShortDot';
        }
      } else {
        // Create a map to track segments and assign different line styles
        const allSeriesData = flatten(values(seriesWithColors));
        const segmentOccurrences = allSeriesData.reduce(
          (acc, chartItem, idx) => {
            if (!acc[chartItem.segment]) {
              acc[chartItem.segment] = [];
            }
            acc[chartItem.segment].push(idx);
            return acc;
          },
          {} as Record<string, number[]>
        );

        // Define available line styles for same segments
        const lineStyles: string[] = [
          'Solid',
          'ShortDash',
          'ShortDot',
          'LongDash',
          'LongDashDot',
          'ShortDashDot',
        ];

        // Determine line style based on segment occurrence
        const segmentIndices = segmentOccurrences[obj.segment];
        const currentIndex = allSeriesData.findIndex(
          (item) =>
            item.segment === obj.segment &&
            item.scenario === obj.scenario &&
            item.dataType === obj.dataType
        );
        const occurrenceIndex = segmentIndices.indexOf(currentIndex);
        dashStyle =
          segmentIndices.length > 1
            ? lineStyles[occurrenceIndex % lineStyles.length]
            : 'Solid'; // Default to solid if segment appears only once
      }
      return {
        ...obj,
        color: scenariosCount > 1 ? adjustColor(color, alphaValue) : color,
        dashStyle,
      };
    });
  }, [
    seriesWithColors,
    segmentsWithColors,
    scenariosCount,
    showScenarios,
    selectedChartDataType,
  ]);

  const groupedSeriesWithColors = useMemo(
    () =>
      scenariosCount <= 1
        ? groupBy(groupedSeriesListWithColors, (i) => i.segment.split(',')[0])
        : groupBy(groupedSeriesListWithColors, 'scenario'),
    [groupedSeriesListWithColors, scenariosCount]
  );

  const groupedSeriesListColors = values(groupedSeriesWithColors);

  const base64Rows = Buffer.from(
    JSON.stringify([
      ...chartData.PublishedData,
      ...chartData.PreviewData,
      Date.now(),
    ])
  ).toString('base64');

  return (
    <Container
      maxWidth={false}
      disableGutters={disableGutters}
      sx={{
        width: '100%',
        background: 'white',
        // v2 fork: small breathing room between the card header chip
        // and the chart's top edge. The toolbar removal left this area
        // feeling cramped.
        pt: 1.5,
      }}
    >
      <Box ref={printChartRef} sx={frameChart ? CHART_FRAME_SX : undefined}>
        {/* v2 fork: chart-type select + chart-data-type select + download
            icon toolbar removed. Agent picks chart type; result is
            read-only display. */}
        {formatKey !== undefined && selectedChartType === 'line' && (
          <LineChart
            chartTitle={chartTitle}
            formatKey={formatKey}
            prefix={prefix}
            suffix={suffix}
            truncation={magnitudeTruncation}
            decimalPlaces={decimalPlaces}
            data={chartData}
            timeScaleType={timescale}
            dateFormat={chartDateType}
            yAxisTitle={yAxisTitle}
            isPanelCollapsed={isPanelCollapsed}
            showScenarios={showScenarios}
            selectedChartDataType={selectedChartDataType}
            groupedChartData={groupedSeriesListWithColors}
          />
        )}
        {formatKey !== undefined && selectedChartType === 'column' && (
          <ColumnChart
            chartTitle={chartTitle}
            formatKey={formatKey}
            prefix={prefix}
            suffix={suffix}
            truncation={magnitudeTruncation}
            decimalPlaces={decimalPlaces}
            data={chartData}
            timeScaleType={timescale}
            dateFormat={chartDateType}
            yAxisTitle={yAxisTitle}
            isPanelCollapsed={isPanelCollapsed}
            showScenarios={showScenarios}
            selectedChartDataType={selectedChartDataType}
            groupedChartData={groupedSeriesListWithColors}
          />
        )}
        {formatKey !== undefined && selectedChartType === 'stackedColumn' && (
          <StackedColumnChart
            chartTitle={chartTitle}
            formatKey={formatKey}
            prefix={prefix}
            suffix={suffix}
            truncation={magnitudeTruncation}
            decimalPlaces={decimalPlaces}
            data={chartData}
            timeScaleType={timescale}
            dateFormat={chartDateType}
            yAxisTitle={yAxisTitle}
            isPanelCollapsed={isPanelCollapsed}
            showScenarios={showScenarios}
            selectedChartDataType={selectedChartDataType}
            groupedChartData={groupedSeriesListWithColors}
          />
        )}
        {formatKey !== undefined &&
          selectedChartType === 'stackedPercentColumn' && (
            <StackedPercentColumnChart
              chartTitle={chartTitle}
              formatKey={formatKey}
              prefix={prefix}
              suffix={suffix}
              truncation={magnitudeTruncation}
              decimalPlaces={decimalPlaces}
              data={chartData}
              timeScaleType={timescale}
              dateFormat={chartDateType}
              yAxisTitle={yAxisTitle}
              isPanelCollapsed={isPanelCollapsed}
              showScenarios={showScenarios}
              selectedChartDataType={selectedChartDataType}
              groupedChartData={groupedSeriesListWithColors}
            />
          )}
        <Grid container spacing={0}>
          <Grid xs={9}>
            <Grid
              container
              rowSpacing={0}
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            >
              {(() => {
                if (scenariosCount <= 1) {
                  return map(groupedSeriesListColors, (item, index) => {
                    return map(filteredItems(item), (serie, i) => {
                      return (
                        <Grid xs={3} key={`${base64Rows}${index}${i}`}>
                          <FormControlLabel
                            sx={{
                              // v2 compression: tighter legend rows. The
                              // MUI FormControlLabel default has 11px
                              // padding around the Checkbox which adds
                              // ~22px per row. Squash it.
                              margin: 0,
                              '& .MuiCheckbox-root, & .TrinityCheckbox-root': {
                                padding: '2px',
                              },
                            }}
                            control={
                              <Checkbox
                                id={`series${serie.id - 1}`}
                                checked={serie.visible}
                                sx={{
                                  color: serie.color,
                                  '&.Mui-checked': {
                                    color: serie.color,
                                  },
                                }}
                                onChange={(e) =>
                                  handleLegendChange(serie.id, e.target.checked)
                                }
                                size="small"
                              />
                            }
                            disableTypography
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{ color: serie.color, fontSize: 12 }}
                                >
                                  {serie.segment}
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                      );
                    });
                  });
                }
                return map(
                  keys(groupedSeriesWithColors),
                  (item, index, arr) => {
                    return (
                      <Grid
                        xs={(() => {
                          if (arr.length === 1) {
                            return 12;
                          } else if (arr.length === 2) {
                            return 6;
                          }
                          return 4;
                        })()}
                        key={item}
                      >
                        <Typography variant="h6">{item}</Typography>
                        {map(groupedSeriesWithColors[item], (serie, i) => {
                          return (
                            <FormGroup key={`${base64Rows}${index}${i}`}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    id={`series${serie.id - 1}`}
                                    checked={serie.visible}
                                    sx={{
                                      color: serie.color,
                                      '&.Mui-checked': {
                                        color: serie.color,
                                      },
                                    }}
                                    onChange={(e) =>
                                      handleLegendChange(
                                        serie.id,
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: '30px',
                                        height: '2px',
                                        backgroundColor:
                                          serie.dashStyle === 'Solid'
                                            ? serie.color
                                            : 'transparent',
                                        backgroundImage:
                                          serie.dashStyle === 'ShortDash'
                                            ? `repeating-linear-gradient(90deg, ${serie.color} 0px, ${serie.color} 3px, transparent 3px, transparent 6px)`
                                            : serie.dashStyle === 'ShortDot'
                                              ? `repeating-linear-gradient(90deg, ${serie.color} 0px, ${serie.color} 1px, transparent 1px, transparent 3px)`
                                              : serie.dashStyle === 'LongDash'
                                                ? `repeating-linear-gradient(90deg, ${serie.color} 0px, ${serie.color} 8px, transparent 8px, transparent 12px)`
                                                : serie.dashStyle ===
                                                    'LongDashDot'
                                                  ? `repeating-linear-gradient(90deg, ${serie.color} 0px, ${serie.color} 8px, transparent 8px, transparent 10px, ${serie.color} 10px, ${serie.color} 11px, transparent 11px, transparent 15px)`
                                                  : serie.dashStyle ===
                                                      'ShortDashDot'
                                                    ? `repeating-linear-gradient(90deg, ${serie.color} 0px, ${serie.color} 3px, transparent 3px, transparent 5px, ${serie.color} 5px, ${serie.color} 6px, transparent 6px, transparent 9px)`
                                                    : 'none',
                                      }}
                                    />
                                    <Typography sx={{ color: serie.color }}>
                                      {serie.segment}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </FormGroup>
                          );
                        })}
                      </Grid>
                    );
                  }
                );
              })()}
            </Grid>
          </Grid>
          <Grid
            xs={3}
            justifyContent="flex-end"
            container
            className="ignore-png"
          >
            <Grid>
              <ChartRangePicker<IChartDataDate>
                type={timescale}
                minRange={head(refreshedChartData.Dates) as IChartDataDate}
                maxRange={last(refreshedChartData.Dates) as IChartDataDate}
                onHandleRangeChange={handleChangeChartRange}
                value={selectedChartRange}
                dates={refreshedChartData.Dates}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <OutputsTable
        ref={tableExportRef ?? outputsTableRef}
        chartData={refreshedChartData}
        formatKey={formatKey as number}
        title={chartTitle}
        prefix={prefix}
        suffix={suffix}
        magnitudeTruncation={magnitudeTruncation}
        decimalPlaces={decimalPlaces}
        outputId={outputId}
        modelInstanceId={modelInstanceId}
        chartDateType={chartDateType}
        timescale={timescale}
        showScenarios={showScenarios}
      />
    </Container>
  );
};

export default GenerateChartPanel;
